/**
 * Asia/Bangkok business-day, settlement-week and accrual-month boundaries.
 *
 * The platform's business day is Bangkok's: the daily check-in day key, the
 * lottery clock the player app shows, the Monday-00:00 weeks that svc-bet
 * rolls up and svc-commission settles, and the calendar months the affiliate
 * producer accrues all name that zone. Every separate copy of
 * that decision is a copy that can drift, and a drifted boundary pays real money
 * into the wrong day or the wrong settlement period, so the zone and the
 * arithmetic live here once.
 *
 * The technique is deliberate: format the instant to a `YYYY-MM-DD` string IN
 * the zone, then read that string back with `fromZonedTime`. Running calendar
 * arithmetic on a `toZonedTime` Date instead runs it through the RUNTIME's
 * calendar, so a host whose own local midnight does not exist on that date (a
 * DST spring-forward at 00:00 — Sao Paulo before 2019, Damascus in the 1990s)
 * moves the boundary by an hour. Never hand-rolled offset arithmetic
 * (CLAUDE.md §3.9): date-fns / date-fns-tz only.
 */
import { addDays, subDays } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { BadRequestError } from './errors.js';

/** The platform's business timezone. Take it from here; never re-type the string. */
export const BANGKOK_TZ = 'Asia/Bangkok';

/** The `YYYY-MM-DD` Bangkok calendar day containing the instant `at`. */
export function bangkokDayKey(at: Date): string {
  return formatInTimeZone(at, BANGKOK_TZ, 'yyyy-MM-dd');
}

/**
 * An instant as Bangkok wall clock, `YYYY-MM-DD HH:mm:ss`.
 *
 * The one shape a timestamp is allowed to take in a file or a log line a human
 * reads. `Date.prototype.toString()` is what it replaces: a CSV export wrote
 * `Thu Aug 13 2026 23:00:00 GMT+0700 (Indochina Time)` into a spreadsheet cell,
 * which no spreadsheet parses as a date, whose zone is whichever one the
 * process happened to run in, and whose month name is English. The zone
 * is pinned here rather than taken from the runtime for the same reason
 * `bangkokDayKey` pins it: the day part must name the same civil day the rest
 * of the row is bucketed under.
 *
 * No `T` and no offset suffix, because the reader is Excel: a space-separated
 * local timestamp is recognised as a datetime, an ISO string with a zone is
 * imported as text.
 */
export function bangkokTimestamp(at: Date): string {
  return formatInTimeZone(at, BANGKOK_TZ, 'yyyy-MM-dd HH:mm:ss');
}

/** The UTC instant of 00:00 Asia/Bangkok on the day containing `at`. */
export function bangkokDayStart(at: Date): Date {
  return fromZonedTime(`${bangkokDayKey(at)} 00:00:00`, BANGKOK_TZ);
}

/** A `YYYY-MM-DD` SHAPE. Shape is not existence — see `bangkokDayWindow`. */
const DAY_NAME = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The half-open UTC window `[start, endExclusive)` of the Bangkok civil day
 * NAMED `day` (`YYYY-MM-DD`).
 *
 * The sibling helpers above take an INSTANT and ask which Bangkok day it fell
 * in. This one takes the day NAME an operator typed into a report filter and
 * answers which instants that day covers, which is the bound a range query
 * needs. `${day}T23:59:59.999Z` is not that bound: it names a UTC day, so on a
 * Bangkok report it drops the last seven hours of the day asked for and counts
 * the first seven of the day before.
 *
 * A day that is well SHAPED but does not exist is rejected, not normalised.
 * `fromZonedTime('2026-02-31 00:00:00')` happily answers 3 March, so a caller
 * that only pattern-matched the shape would run a money query over a window
 * nobody asked for and report it as the day that was asked for — silently, and
 * three days out. The round trip through `bangkokDayKey` is the existence
 * check: only a real calendar day formats back to its own name. Callers on an
 * HTTP boundary get a 400, which is what the raw string used to earn from
 * Postgres (22008) before the bound was computed here.
 *
 * The end is EXCLUSIVE and is stepped from Bangkok noon, not from `start`:
 * `addDays` walks the RUNTIME's calendar, so a DST transition in the process
 * timezone moves a midnight step by an hour. An hour either side of noon is
 * still the same Bangkok day.
 */
export function bangkokDayWindow(day: string): { start: Date; endExclusive: Date } {
  const start = fromZonedTime(`${day} 00:00:00`, BANGKOK_TZ);
  // Order matters: `bangkokDayKey` throws a RangeError on an Invalid Date, so
  // the shape and the NaN check have to short-circuit ahead of it.
  if (!DAY_NAME.test(day) || Number.isNaN(start.getTime()) || bangkokDayKey(start) !== day) {
    throw new BadRequestError(
      'TIME-001',
      'Invalid day',
      `not a calendar day in ${BANGKOK_TZ}: "${day}"`,
    );
  }
  const noon = fromZonedTime(`${day} 12:00:00`, BANGKOK_TZ);
  return {
    start,
    endExclusive: fromZonedTime(`${bangkokDayKey(addDays(noon, 1))} 00:00:00`, BANGKOK_TZ),
  };
}

/**
 * The Bangkok calendar day `daysBack` days before the one containing `at`.
 *
 * Day arithmetic runs from Bangkok *noon*, not from midnight: `subDays` walks
 * the runtime's local calendar, so a DST transition in the process timezone can
 * move the result by an hour. An hour either side of noon is still the same
 * Bangkok day; an hour either side of midnight is not.
 */
export function bangkokDayKeyBefore(at: Date, daysBack: number): string {
  const noon = fromZonedTime(`${bangkokDayKey(at)} 12:00:00`, BANGKOK_TZ);
  return bangkokDayKey(subDays(noon, daysBack));
}

/** The UTC instant of 00:00 Asia/Bangkok `daysBack` days before the day containing `at`. */
export function bangkokDayStartBefore(at: Date, daysBack: number): Date {
  return fromZonedTime(`${bangkokDayKeyBefore(at, daysBack)} 00:00:00`, BANGKOK_TZ);
}

/**
 * The Bangkok calendar day `daysForward` days after the one containing `at`.
 * Noon-anchored for the same reason as `bangkokDayKeyBefore`.
 */
function bangkokDayKeyAfter(at: Date, daysForward: number): string {
  const noon = fromZonedTime(`${bangkokDayKey(at)} 12:00:00`, BANGKOK_TZ);
  return bangkokDayKey(addDays(noon, daysForward));
}

/** The UTC instant of 00:00 Asia/Bangkok `daysForward` days after the day containing `at`. */
export function bangkokDayStartAfter(at: Date, daysForward: number): Date {
  return fromZonedTime(`${bangkokDayKeyAfter(at, daysForward)} 00:00:00`, BANGKOK_TZ);
}

/**
 * The Bangkok calendar day NAMED `key` (`YYYY-MM-DD`) at 00:00, as a UTC instant.
 *
 * `bangkokDayWindow` answers the same question for a day name an OPERATOR
 * typed, and validates it because a well-shaped day that does not exist would
 * silently move a money window. This one is for a key the code just built from
 * numbers it clamped itself, where a throw would be dead weight — take
 * `bangkokDayWindow(key).start` for anything that came off the wire.
 */
export function bangkokDayKeyStart(key: string): Date {
  return fromZonedTime(`${key} 00:00:00`, BANGKOK_TZ);
}

/**
 * The UTC instant of the Monday-00:00 Bangkok week-start that contains `at`.
 *
 * Weeks start on Monday to match the settlement period grid: the weekly MLM
 * settlement closes one Bangkok week at a time, so a period strictly before the
 * current open week is already settled and its rollup bucket is frozen.
 */
export function bangkokWeekStart(at: Date = new Date()): Date {
  const isoDayOfWeek = Number(formatInTimeZone(at, BANGKOK_TZ, 'i'));
  return bangkokDayStartBefore(at, isoDayOfWeek - 1);
}

/**
 * The UTC instant of 00:00 Asia/Bangkok on the most recent `weekday` (0..6,
 * Sun..Sat) on or before the Bangkok day containing `at` — the start of a week
 * that opens on that weekday, such as a master-agent's clearing day.
 * `bangkokWeekStartOn(at, 1)` equals `bangkokWeekStart(at)`. Throws a
 * RangeError for anything that is not an integer 0..6.
 */
export function bangkokWeekStartOn(at: Date, weekday: number): Date {
  if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
    throw new RangeError(`weekday must be an integer 0..6 (Sun..Sat), got ${weekday}`);
  }
  const dayOfWeek = Number(formatInTimeZone(at, BANGKOK_TZ, 'i')) % 7;
  return bangkokDayStartBefore(at, (dayOfWeek - weekday + 7) % 7);
}

/**
 * The UTC instant of the 1st 00:00 Asia/Bangkok of the month containing `at`.
 *
 * The monthly affiliate accrual period is a Bangkok calendar month, so it gets
 * the same string-first treatment as the day and the week: format the instant to
 * `YYYY-MM` in the zone and read `YYYY-MM-01 00:00:00` back. The tempting
 * `startOfMonth(toZonedTime(at, BANGKOK_TZ))` sets 00:00 on the RUNTIME's
 * calendar, so a host whose own midnight does not exist on the 1st moves the
 * boundary an hour — America/Asuncion on 2023-10-01 is a real instance, and an
 * hour before a Bangkok month start belongs to the previous month.
 */
export function bangkokMonthStart(at: Date = new Date()): Date {
  return fromZonedTime(`${formatInTimeZone(at, BANGKOK_TZ, 'yyyy-MM')}-01 00:00:00`, BANGKOK_TZ);
}

/** The four units a compact duration is spelled in, largest first. */
export type DurationUnit = 'd' | 'h' | 'm' | 's';

/** One count and the unit it counts, with no label attached. */
export interface DurationPart {
  value: number;
  unit: DurationUnit;
}

/**
 * A span of seconds as its two largest units — "2 d 3 h", "3 h 37 m" — without
 * saying how to spell them.
 *
 * The labels are DELIBERATELY absent. Both SPAs render this, and neither may
 * take unit names from date-fns: it ships no Lao or Burmese locale, so two of
 * the seven languages would silently read English. Each app maps the unit key
 * onto its own i18n catalogue (`time.d/h/m/s` in `common`) and keeps the
 * `formatDurationShort(totalSeconds, t)` signature its callers already use;
 * what lives here is only the arithmetic, which had been copied verbatim from
 * the player app into the back office.
 *
 * Zero and anything negative read as "0 minutes", not "0 seconds". A clock skew
 * between an operator's machine and the database surfaces as a small negative
 * age, and minutes is the unit the surfaces that show ages are read in.
 */
export function durationParts(totalSeconds: number): DurationPart[] {
  const total = Math.max(0, Math.floor(totalSeconds));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (total === 0) return [{ value: 0, unit: 'm' }];
  if (d > 0) return [{ value: d, unit: 'd' }, { value: h, unit: 'h' }];
  if (h > 0) return [{ value: h, unit: 'h' }, { value: m, unit: 'm' }];
  if (m > 0) return [{ value: m, unit: 'm' }, { value: s, unit: 's' }];
  return [{ value: s, unit: 's' }];
}
